import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  ContentChildren,
  QueryList,
  Inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Dictionary, uuid, isNullOrEmpty } from '@awp/core/utils';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, delay, map, switchMap, tap, mapTo, startWith, take } from 'rxjs/operators';

import { ContentParser } from '../../content.parser';
import { ContentService } from '../../content.service';
import { ContentOutletDirective } from '../../directives';
import { CONTENT_FRAGMENT_PROVIDER, ContentFragmentProvider } from '../../content-fragment.provider';

export interface ContentFragment {
  type?: 'html' | 'component'
  componentName?: string;
  href?: string;
}

@Component({
  selector: 'ap-content',
  templateUrl: './content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ContentComponent implements OnInit, OnChanges, AfterContentInit, AfterViewInit, OnDestroy {

  private _componentDefs: Map<string, ContentOutletDirective> = new Map();
  private _contentChanges$ = new Subject();
  private _fragmentChanges$ = new Subject();
  private _contentOutletChanges$ = new Subject();
  private _fragments: Dictionary<ContentFragment>;
  safeContent: SafeHtml;

  @Input() name: string;
  @Input() key: string;
  @Input('value') content: any;
  @Input() params: Dictionary<any>;
  @Output() startLoading = new EventEmitter<void>();
  @Output() finishLoading = new EventEmitter<void>();
  @Output() loading = new EventEmitter<boolean>();

  @ViewChild('containerContent', { static: true, read: ElementRef }) containerContent: ElementRef<HTMLDivElement>;
  @ContentChildren(ContentOutletDirective) contentOutlets: QueryList<ContentOutletDirective>;

  constructor(
    private render: Renderer2,
    private injector: Injector,
    private contentService: ContentService,
    private contentParser: ContentParser,
    @Inject(CONTENT_FRAGMENT_PROVIDER) fragmentProvider: ContentFragmentProvider,
    private parser: ContentParser,
    private sanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef) {

    combineLatest(this._contentChanges$, this._fragmentChanges$, this._contentOutletChanges$)
      .pipe(
        tap(() => this.changeDetectorRef.markForCheck()),
        debounceTime(500),
        tap(() => {
          this.startLoading.emit();
          this.loading.emit(true)
        }),
        switchMap(() => {
          console.log(`${this.name} Combine Latest Notify Changed`);
          return this.notifyOutletChanged()
            .pipe(
              tap(() => {
                this.finishLoading.emit();
                this.loading.emit(false);
              })
            );
        }),
        untilDestroyed(this)
      )
      .subscribe(p => {
        this.changeDetectorRef.markForCheck();
      });

    fragmentProvider.fragments
      .pipe(take(1))
      .subscribe(fragments => {
        this._fragments = fragments;
        this._fragmentChanges$.next();
      });
  }

  ngOnInit(): void {
    this.loading.emit(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.content != null && changes.content.currentValue !== changes.content.previousValue) {
      this._contentChanges$.next();
    }
    if (changes.key != null && changes.key.currentValue !== changes.key.previousValue) {
      this._contentChanges$.next();
    }
  }

  ngAfterContentInit(): void {
    this.contentOutlets.changes
      .pipe(
        startWith(this.contentOutlets.toArray()),
        untilDestroyed(this)
      )
      .subscribe(p => {
        this._contentOutletChanges$.next();
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.loading.emit(false), 500);
  }

  ngOnDestroy(): void {
    if (this._componentDefs != null) {
      this._componentDefs.forEach(p => p.ngOnDestroy());
    }
  }

  private notifyOutletChanged(): Observable<any> {
    const el = this.render.createElement('div') as HTMLDivElement;

    return of(el).pipe(
      switchMap((element: HTMLDivElement) => {
        if (this.content != null) {
          element.innerHTML = this.parser.interpolate(this.content, this.params);
        } else if (!isNullOrEmpty(this.key)) {
          return this.getContentFragment(this.key)
                  .pipe(
                    tap(content => element.innerHTML = content),
                    mapTo(element)
                  );
        }

        return of(element);
      }),
      switchMap((element: HTMLDivElement) => {
        const contentElements = this.getElementByType(element, 'content');

        if (contentElements.size > 0) {
          // Need to fetch content fragments from CMS
          const fragmentKeys = [...contentElements.keys()];
          return forkJoin(fragmentKeys.map(p => this.getContentFragment(p)))
            .pipe(
              tap(fragments => {
                fragmentKeys.forEach((value, index) => {
                  const contentFragment = fragments[index];
                  if (contentFragment != null) {
                    const elements = contentElements.get(value);
                    elements.forEach(el => el.innerHTML = contentFragment);
                  }
                });
              }),
              map(fragments => element)
            );
        } else {
          return of(element);
        }
      }),
      tap(element => {
        // Need to clear inner content from data-component as it includes extra markup.
        const componentElements = this.getElementByType(element, 'component');
        componentElements.forEach((elementRefs) => {
          elementRefs.forEach(el => el.innerHTML = '');
        });

        //Get all content as text for angular sanitisation before inject it into the page.
        this.safeContent = this.sanitizer.bypassSecurityTrustHtml(element.innerHTML); // Todo: Should possible use sanitize method here instead.
        this.changeDetectorRef.markForCheck();
      }),
      delay(500), // Need to delay so change detection will run
      tap(() => this.createComponentOutlets(this.containerContent.nativeElement)),
      tap(() => this.createContentOutlets(this.containerContent.nativeElement))
    );
  }

  private createComponentOutlets(elementRef: HTMLElement): void {
    const componentRefs = this.getElementByType(elementRef, 'component');
    const refIds = new Set<string>();

    componentRefs.forEach((elements, key) => {
      const { componentName } = this.getFragment(key, 'component');

      elements.forEach(element => {
        const componentInstance = this.getInjectedComponent(element);

        if (componentInstance != null) {
          refIds.add(componentInstance.outletId);
        }
        else if (componentName != null) {
          element.innerHTML = '';
          const component = new ContentOutletDirective(this.injector);
          component.componentName = componentName;
          component.outletId = `${key}:${uuid()}`;
          component.createComponent(element);
          this.appendDataElement(element, 'component', component.outletId);
          this._componentDefs.set(component.outletId, component);
          refIds.add(component.outletId);
        } else {
          console.warn(`data-component ${key} not found`);
        }
      });
    });

    // Need to remove components that no longer exist in list.
    const removedIds = [...this._componentDefs.keys()].filter(id => !refIds.has(id));
    removedIds.forEach(p => {
      const outlet = this._componentDefs.get(p);
      outlet.destroyComponent();
      this._componentDefs.delete(p);
    });

    this.changeDetectorRef.markForCheck();
  }

  private createContentOutlets(elementRef: HTMLElement): void {
    if (elementRef == null || this.contentOutlets == null || this.contentOutlets.length == 0) return;

    const contentElements = this.getElementByType(elementRef, 'content');

    this.contentOutlets.forEach(p => {
      const item = contentElements.get(p.outletId);
      if (item != null && item.length > 0) {
        p.createComponent(item[0]);
      }
    });
  }

  private getFragment(id: string, type: 'html' | 'component'): ContentFragment | null {
    if (this._fragments == null) {
      return { };
    }

    const item = this._fragments[id];
    return item != null && item.type === type ? item : { };
  }

  private getInjectedComponent(elementRef: HTMLElement): ContentOutletDirective | null {
    const id = this.getDataElement(elementRef);
    if (id != null) {
      const componentRef = this._componentDefs.get(id);
      if (componentRef == null) {
        console.warn('Component not found');
      } else {
        return componentRef;
      }
    }
    return null;
  }

  private appendDataElement(elementRef: HTMLElement, type: 'component' | 'content', id: string): void {
    const componentComment = this.render.createComment(`data-${type}="${id}"`);
    elementRef.append(componentComment);
  }

  private getDataElement(elementRef: HTMLElement): string | null {
    if (elementRef.childNodes.length > 0) {
      const firstChild = elementRef.childNodes[0];
      if (firstChild.nodeType === 8) { // nodeType 8 is a comment node
        const id = firstChild.nodeValue.substr('data-component='.length).replace(/"/g, '');
        return id;
      }
    }
    return null;
  }

  private getElementByType(elementRef: HTMLElement, type: 'content' | 'component'): Map<string, HTMLElement[]> {
    const elements = elementRef.querySelectorAll(`div[data-${type}]`);
    const result = new Map<string, HTMLElement[]>();

    elements.forEach((element: HTMLElement) => {
      const attr = element.attributes.getNamedItem(`data-${type}`);
      const entry = result.get(attr.value);
      if (entry == null) {
        result.set(attr.value, [element]);
      } else {
        entry.push(element);
      }
    });

    return result;
  }

  private getContentFragment(id: string): Observable<string> {
    if (this._fragments == null) {
      return of(null);
    }

    const fragment = this._fragments[id];

    if (fragment != null && fragment.type === 'html') {
      return this.contentService.getContent(fragment.href, null, 'html')
        .pipe(
          map(response => {
            return this.contentParser.interpolate(response, this.params);
          }),
          catchError(err => {
            console.error(`Error fetching content fragment '${name}'`);
            return of(null);
          })
        );
    } else {
      return of(null);
    }
  }

}
